/**
 * Passport number format validator.
 *
 * Phase 03 Plan 03-06 Task 1 (EMP-07). Passport numbers vary wildly across
 * issuing countries (US 9 digits, UK 9 alphanumeric, Egypt 8, Philippines 9,
 * India 8 with letter prefix, etc.), so we enforce only a minimal structural
 * contract: at least 6 characters, alphanumeric only, no whitespace.
 *
 * Format contract (03-RESEARCH §D-10):
 *   - Length ≥ 6 (reasonable lower bound covering all MENA + Asia issuers)
 *   - `[A-Za-z0-9]` only (no spaces, no dashes, no control chars)
 *
 * Rejects: shorter than 6 chars, contains punctuation/whitespace/Arabic.
 *
 * MUST-test per project conventions §Testing Philosophy — ID validators are
 * data-integrity critical.
 */
import { z } from "zod";

/** `^[A-Za-z0-9]+$` — alphanumeric only (no whitespace / punctuation). */
export const PASSPORT_ALNUM_PATTERN = /^[A-Za-z0-9]+$/;

/** Minimum reasonable passport length across MENA/Asia issuing authorities. */
export const PASSPORT_MIN_LENGTH = 6;

export const passportSchema = z
  .string()
  .min(PASSPORT_MIN_LENGTH, {
    message: `Passport number must be at least ${PASSPORT_MIN_LENGTH} characters`,
  })
  .regex(PASSPORT_ALNUM_PATTERN, {
    message: "Passport number must be alphanumeric (letters and digits only)",
  });

export type Passport = z.infer<typeof passportSchema>;
